import Papa from 'papaparse';
import { AnalyticsSummary, QRCodeData } from '../types';

interface ExportOptions {
  qrCode: QRCodeData;
  analytics: AnalyticsSummary | undefined;
}

export function exportToCsv({ qrCode, analytics }: ExportOptions): void {
  if (!analytics) {
    console.warn('No analytics data to export');
    return;
  }

  // Create multiple sheets of data
  const sheets: { name: string; data: Record<string, string | number>[] }[] = [];

  // Summary sheet
  sheets.push({
    name: 'Summary',
    data: [
      { Metric: 'QR Code Title', Value: qrCode.title },
      { Metric: 'Total Scans', Value: analytics.total_scans },
      { Metric: 'Unique Users', Value: analytics.unique_users },
      { Metric: 'Unique Rate (%)', Value: analytics.total_scans > 0
        ? ((analytics.unique_users / analytics.total_scans) * 100).toFixed(1)
        : '0' },
      { Metric: 'Last Scan', Value: analytics.last_scan_at
        ? new Date(analytics.last_scan_at).toLocaleString()
        : 'Never' },
      { Metric: 'Fallback URL', Value: qrCode.fallback_url },
      { Metric: 'Created', Value: new Date(qrCode.created_at).toLocaleDateString() },
    ],
  });

  // Daily scans sheet
  if (analytics.daily_scans?.length > 0) {
    sheets.push({
      name: 'Daily_Scans',
      data: analytics.daily_scans.map(item => ({
        Date: item.date,
        Scans: item.count,
      })),
    });
  }

  // Device breakdown sheet
  if (analytics.device_breakdown?.length > 0) {
    sheets.push({
      name: 'Device_Breakdown',
      data: analytics.device_breakdown.map(item => ({
        Device: item.device,
        Scans: item.count,
        Percentage: analytics.total_scans > 0
          ? ((item.count / analytics.total_scans) * 100).toFixed(1) + '%'
          : '0%',
      })),
    });
  }

  // Browser breakdown sheet
  if (analytics.browser_breakdown?.length > 0) {
    sheets.push({
      name: 'Browser_Breakdown',
      data: analytics.browser_breakdown.map(item => ({
        Browser: item.browser,
        Scans: item.count,
        Percentage: analytics.total_scans > 0
          ? ((item.count / analytics.total_scans) * 100).toFixed(1) + '%'
          : '0%',
      })),
    });
  }

  // Country breakdown sheet
  if (analytics.country_breakdown?.length > 0) {
    sheets.push({
      name: 'Country_Breakdown',
      data: analytics.country_breakdown.map(item => ({
        Country: item.country,
        Scans: item.count,
        Percentage: analytics.total_scans > 0
          ? ((item.count / analytics.total_scans) * 100).toFixed(1) + '%'
          : '0%',
      })),
    });
  }

  // Hourly breakdown sheet
  if (analytics.hourly_breakdown?.length > 0) {
    sheets.push({
      name: 'Hourly_Activity',
      data: analytics.hourly_breakdown.map(item => ({
        Hour: `${item.hour.toString().padStart(2, '0')}:00`,
        Scans: item.count,
      })),
    });
  }

  // URL breakdown sheet (if rules-based)
  if (analytics.url_breakdown?.length > 0) {
    sheets.push({
      name: 'URL_Performance',
      data: analytics.url_breakdown.map(item => ({
        URL: item.url,
        Scans: item.count,
        Percentage: analytics.total_scans > 0
          ? ((item.count / analytics.total_scans) * 100).toFixed(1) + '%'
          : '0%',
      })),
    });
  }

  // Combine all sheets into a single CSV with section headers
  const allData: string[] = [];

  sheets.forEach((sheet, index) => {
    if (index > 0) {
      allData.push(''); // Empty line between sections
    }
    allData.push(`=== ${sheet.name.replace(/_/g, ' ')} ===`);

    const csv = Papa.unparse(sheet.data, {
      header: true,
      skipEmptyLines: true,
    });
    allData.push(csv);
  });

  const finalCsv = allData.join('\n');

  // Create and download file
  const blob = new Blob([finalCsv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${qrCode.title.replace(/[^a-zA-Z0-9]/g, '_')}_analytics_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
